import { FeatureName } from '@/features.config'
import { useFeatureAvailability } from '@/hooks'
import { ReactNode, Suspense } from 'react'
import { MobileWarningBanner } from '../MobileWarningBanner'

interface ResponsiveFeatureProps {
  feature: FeatureName
  children: ReactNode
  fallback?: ReactNode
  loadingFallback?: ReactNode
}

/**
 * Wrapper component for conditional rendering based on viewport availability.
 * Renders the responsive version (children) when the feature is available for
 * the current viewport. When the feature is not available (non-desktop device),
 * it shows a dismissible warning banner above the children content.
 *
 * Supports code splitting via React.lazy components.
 *
 * @example
 * // With code splitting
 * const ResponsiveLogin = React.lazy(() => import('./ResponsiveLogin'))
 * const DesktopLogin = React.lazy(() => import('./DesktopLogin'))
 *
 * <ResponsiveFeature
 *   feature={FEATURES.AUTH}
 *   fallback={<DesktopLogin />}
 * >
 *   <ResponsiveLogin />
 * </ResponsiveFeature>
 *
 * @example
 * // Without code splitting
 * <ResponsiveFeature
 *   feature={FEATURES.AUTH}
 *   fallback={<DesktopLogin />}
 * >
 *   <ResponsiveLogin />
 * </ResponsiveFeature>
 */
export const ResponsiveFeature = ({
  feature,
  children,
  fallback,
  loadingFallback = null,
}: ResponsiveFeatureProps): JSX.Element => {
  const { isAvailable } = useFeatureAvailability(feature)

  if (isAvailable) {
    return <Suspense fallback={loadingFallback}>{children}</Suspense>
  }

  const content = fallback ?? children

  return (
    <Suspense fallback={loadingFallback}>
      <MobileWarningBanner />
      {content}
    </Suspense>
  )
}
